import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, MessageCircle, Send } from "lucide-react";
import { NAV } from "@/data/site";
import { waLink } from "@/lib/api";
import { useLang, LangToggle } from "@/i18n/LangContext";

const CLINIC_LINKS = [
  { id: "all", to: "/locations", en: "All locations", es: "Todas las sedes", subEn: "Maps, hours & contact", subEs: "Mapas, horarios y contacto" },
  { id: "sjds", to: "/dentist-san-juan-del-sur", en: "Dentist in San Juan del Sur", es: "Dentista en San Juan del Sur", subEn: "On the beach · +505 8632 1846", subEs: "En la playa · +505 8632 1846" },
  { id: "managua", to: "/dentist-managua", en: "Dentist in Managua", es: "Dentista en Managua", subEn: "Plaza la Reunión · +505 8941 5471", subEs: "Plaza la Reunión · +505 8941 5471" },
];

const linkCls = ({ isActive }) =>
  `whitespace-nowrap text-[12px] xl:text-[13px] uppercase tracking-[0.16em] transition-colors duration-300 ${isActive ? "text-[#00C9B7]" : "text-slate-300 hover:text-white"}`;

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { pathname } = useLocation();
  const { tr, pick } = useLang();

  useEffect(() => { setOpen(false); }, [pathname]);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <motion.header
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
      className={`fixed top-0 inset-x-0 z-50 transition-[background-color,backdrop-filter] duration-500 ${scrolled || open ? "bg-[#0B0F12]/85 backdrop-blur-xl border-b border-[#00C9B7]/10" : "bg-transparent"}`}
      data-testid="site-navbar"
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8 h-20 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-3" data-testid="nav-logo-link">
          <span className="w-11 h-11 rounded-full bg-[#1AA5A0] flex items-center justify-center ring-1 ring-[#00C9B7]/40 shrink-0"><img src="/img/tooth.png" alt="SunCare Dental logo" className="w-9 h-9 object-contain" data-testid="nav-logo-img" /></span>
          <span className="font-display text-base min-[400px]:text-lg sm:text-2xl text-[#1AA5A0] whitespace-nowrap">Dr. Cora Baltodano</span>
        </Link>
        <nav className="hidden lg:flex items-center gap-5 xl:gap-7">
          {NAV.map((n) => n.to === "/locations" ? (
            <div key={n.to} className="relative group" data-testid="nav-locations-dropdown">
              <NavLink to={n.to} className={linkCls} data-testid="nav-link-locations">{pick(n, "label")} ▾</NavLink>
              <div className="absolute left-1/2 -translate-x-1/2 top-full pt-4 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-opacity duration-200">
                <div className="w-64 rounded-2xl bg-[#131B21]/95 backdrop-blur-xl border border-[#00C9B7]/20 p-2 shadow-2xl">
                  {CLINIC_LINKS.map((c) => (
                    <Link key={c.to} to={c.to} className="block rounded-xl px-4 py-3 hover:bg-[#00C9B7]/10 transition-colors" data-testid={`nav-clinic-${c.id}`}>
                      <span className="block text-sm font-semibold text-white">{tr(c.en, c.es)}</span>
                      <span className="block text-xs text-slate-400">{tr(c.subEn, c.subEs)}</span>
                    </Link>
                  ))}
                </div>
              </div>
            </div>
          ) : (
            <NavLink key={n.to} to={n.to} end={n.to === "/"} className={linkCls} data-testid={`nav-link-${n.label.toLowerCase().replace(/\s/g, "-")}`}>{pick(n, "label")}</NavLink>
          ))}
          <LangToggle />
          <a href={waLink()} target="_blank" rel="noreferrer" className="whitespace-nowrap rounded-full bg-[#00C9B7] text-[#031312] px-5 py-2.5 text-xs font-bold uppercase tracking-widest hover:bg-[#00E0CC] transition-colors" data-testid="nav-book-now-btn">{tr("Book Now", "Reservar")}</a>
        </nav>
        <div className="lg:hidden flex items-center gap-2">
          <LangToggle />
          <button className="text-white p-2" onClick={() => setOpen(!open)} aria-label="Menu" data-testid="nav-mobile-toggle">{open ? <X /> : <Menu />}</button>
        </div>
      </div>
      <AnimatePresence>
        {open && (
          <motion.nav initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} className="lg:hidden overflow-hidden px-6 pb-6 flex flex-col gap-5" data-testid="nav-mobile-menu">
            {NAV.map((n, i) => (
              <motion.div key={n.to} initial={{ x: -30, opacity: 0 }} animate={{ x: 0, opacity: 1 }} transition={{ delay: i * 0.05 }}>
                <NavLink to={n.to} end={n.to === "/"} className={linkCls} data-testid={`mobile-nav-${n.label.toLowerCase().replace(/\s/g, "-")}`}>{pick(n, "label")}</NavLink>
                {n.to === "/locations" && (
                  <div className="mt-3 ml-4 flex flex-col gap-3 border-l border-[#00C9B7]/30 pl-4">
                    {CLINIC_LINKS.slice(1).map((c) => <Link key={c.to} to={c.to} className="text-sm text-slate-300 hover:text-white" data-testid={`mobile-clinic-${c.id}`}>{tr(c.en, c.es)}</Link>)}
                  </div>
                )}
              </motion.div>
            ))}
            <motion.div initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ delay: 0.35 }} className="pt-5 border-t border-white/10 space-y-3" data-testid="mobile-contact-cta">
              <p className="font-display uppercase text-2xl">{tr("Contact us now", "Contáctanos ahora")}</p>
              <p className="text-sm text-slate-400">{tr("Questions or in pain? We'll get you on our schedule.", "¿Preguntas o dolor? Te daremos una cita pronto.")}</p>
              <Link to="/contact-me" className="flex items-center justify-center gap-2 rounded-full bg-[#00C9B7] text-[#031312] py-3.5 text-xs font-bold uppercase tracking-widest" data-testid="mobile-contact-now-btn"><Send size={15} /> {tr("Contact us now", "Contáctanos ahora")}</Link>
              <a href={waLink()} target="_blank" rel="noreferrer" className="flex items-center justify-center gap-2 rounded-full bg-[#25D366] text-white py-3.5 text-xs font-bold uppercase tracking-widest" data-testid="mobile-whatsapp-btn"><MessageCircle size={15} /> WhatsApp</a>
            </motion.div>
          </motion.nav>
        )}
      </AnimatePresence>
    </motion.header>
  );
}
